import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Loader2 } from 'lucide-react';
import BrandWatermark from '@/components/BrandWatermark';

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_sales-analytics-base/artifacts/bju11je1_Final%20Logo-01.webp";

export default function Login() {
    const { user, loading, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
    }
    if (user) return <Navigate to="/" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr(null); setBusy(true);
        try {
            await login(email.trim(), password);
            nav('/', { replace: true });
        } catch (e) {
            // Prefer server-side JSON detail; fall back to helpful message otherwise.
            const detail = e.response?.data?.detail;
            let msg = formatApiErrorDetail(detail);
            if (!detail) {
                if (e.response?.status && e.response.status >= 500) {
                    msg = 'Service is temporarily unavailable (server error). Please try again in a moment.';
                } else if (!e.response) {
                    msg = 'Cannot reach the server. Check your connection and try again.';
                } else {
                    msg = e.message || msg;
                }
            }
            setErr(msg);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center px-4 relative overflow-hidden">
            {/* Brand swirling watermark — subtle, top-left */}
            <BrandWatermark size={520} opacity={0.12} className="absolute -top-20 -left-24" />
            <BrandWatermark size={420} opacity={0.08} className="absolute -bottom-24 -right-20 rotate-180" />

            <div className="relative w-full max-w-md" data-testid="login-page">
                <div className="text-center mb-8">
                    <img
                        src={LOGO_URL}
                        alt="Ikas Corp"
                        data-testid="login-logo"
                        className="w-full max-w-xs h-auto mx-auto"
                    />
                    <div className="overline mt-6 text-[#016a6d]">Sales CRM</div>
                </div>

                <div className="surface-1 rounded-md p-8">
                    <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Sign in</h1>
                    <p className="text-sm text-slate-500 mb-6">Access your pipeline dashboard.</p>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="overline block mb-1">Email</label>
                            <Input
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                data-testid="login-email"
                                className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-10"
                                autoFocus
                                required
                            />
                        </div>
                        <div>
                            <label className="overline block mb-1">Password</label>
                            <Input
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                data-testid="login-password"
                                className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-10"
                                required
                            />
                        </div>
                        {err && (
                            <div data-testid="login-error" className="text-sm text-[#FF453A] bg-[#FF453A]/10 border border-[#FF453A]/30 rounded p-3">
                                {err}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={busy}
                            data-testid="login-submit"
                            className="w-full h-10 bg-[#016a6d] hover:bg-[#014e50] gap-2"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            {busy ? 'Signing in…' : 'Sign in'}
                        </Button>
                    </form>
                </div>

                <div className="text-center text-xs text-slate-400 mt-6">
                    Ikas Corp · Bengaluru · FY 2026
                </div>
            </div>
        </div>
    );
}
